import { Controller } from "@hotwired/stimulus"

// Synchronizuje pola formularza z JSON-em w textarea fields.
// Obsługuje dwa tryby:
// 1. Obiekt (fields) — input z data-key="brand_color" ↔ fields JSON { "brand_color": "xxx" }
//    data-key z kropkami to ścieżka w zagnieżdżonym JSON-ie: data-key="organize.post.meeting_template_id" ↔
//    { "organize": { "post": { "meeting_template_id": "12" } } } (ustawienia czytane przez settings_value)
// 2. Tablica (domains) — input z data-array-for="product_app_domains" ↔ textarea z JSON ["xxx"]
//    wartość inputa = pierwszy element tablicy, wpisanie zmienia tablicę na [value]
//
// Użycie:
//   <form data-controller="fields-form">
//     <%# obiekt — fields %>
//     <textarea data-fields-form-target="json" id="fields_json">...</textarea>
//     <input data-fields-form-target="field" data-key="brand_color"
//            data-action="input->fields-form#fieldChanged">
//
//     <%# tablica — domains %>
//     <textarea data-fields-form-target="arrayJson" id="product_app_domains">...</textarea>
//     <input data-fields-form-target="arrayField" data-array-for="product_app_domains"
//            data-action="input->fields-form#arrayFieldChanged">
//   </form>
//
// Niepoprawny JSON w edytorze blokuje zapis (komunikat z data-fields-form-invalid-json-message-value) -
// inaczej merge przy submit zacząłby od pustego obiektu/tablicy i nadpisał resztę kluczy
export default class extends Controller {
  static targets = ["json", "field", "arrayJson", "arrayField"]
  static values = { invalidJsonMessage: String }

  connect() {
    this.updating = false
    this.syncFromJson()
    this.syncArrayFields()
    // słuchamy na formie: kontroler bywa na wewnętrznym divie, a submit nie schodzi do dzieci;
    // ref trzymamy, bo bind() tworzy nową funkcję i removeEventListener by jej nie zdjął
    this.boundMergeBeforeSubmit = this.mergeBeforeSubmit.bind(this)
    this.form = this.element.closest("form") || this.element
    this.form.addEventListener("submit", this.boundMergeBeforeSubmit)
  }

  disconnect() {
    this.form?.removeEventListener("submit", this.boundMergeBeforeSubmit)
  }

  // --- Obiekt (fields) ---

  syncFromJson() {
    const data = this.parseJson()
    if (!data) return

    this.fieldTargets.forEach((input) => {
      const key = input.dataset.key
      const value = key ? readKey(data, key) : undefined
      if (value !== undefined) {
        if (input.dataset.multiple === "true") {
          // multi-select (grupa checkboxów): wartość to tablica - zaznaczamy checkbox, gdy jego wartość jest w tablicy
          const selected = (Array.isArray(value) ? value : [value]).map(String)
          input.checked = selected.includes(input.value)
        } else if (input.type === "checkbox") {
          input.checked = value === true
        } else {
          input.value = value
        }
      }
    })
  }

  fieldChanged() {
    if (this.updating) return
    this.updating = true
    this.mergeFieldsToJson()
    this.updating = false
  }

  jsonChanged() {
    if (this.updating) return
    this.updating = true
    this.syncFromJson()
    this.updating = false
  }

  mergeBeforeSubmit(event) {
    const invalid = this.invalidJsonTextarea()
    if (invalid) {
      event.preventDefault()
      event.stopImmediatePropagation()
      this.showInvalidJson(invalid)
      return
    }
    this.mergeFieldsToJson()
    this.mergeArrayFieldsToJson()
  }

  // pierwsza textarea z niepoprawnym JSON-em (fields musi być obiektem, arrayJson tablicą) albo null
  invalidJsonTextarea() {
    if (this.hasJsonTarget && !isValidJson(readRaw(this.jsonTarget), "object")) return this.jsonTarget
    const arrays = this.arrayFieldTargets.map((input) => this.findArrayTextarea(input)).filter(Boolean)
    return arrays.find((textarea) => !isValidJson(readRaw(textarea), "array")) || null
  }

  showInvalidJson(textarea) {
    // edytor JSON bywa na nieaktywnej zakładce formatki (np. Zaawansowane w projekcie)
    window.IntumJs?.revealTab?.(textarea)
    const cmWrapper = textarea.closest("[data-controller='codemirror']")
    const focusable = cmWrapper?.cmView || textarea
    focusable.focus?.()
    if (!this.invalidJsonMessageValue) return

    // tekst przez textContent - notify wstawia HTML
    const holder = document.createElement("span")
    holder.textContent = this.invalidJsonMessageValue
    window.IntumJs?.notify(holder.innerHTML, "error")
  }

  mergeFieldsToJson() {
    if (!this.hasJsonTarget) return
    const data = this.parseJson() || {}
    const multipleDone = new Set()

    this.fieldTargets.forEach((input) => {
      const key = input.dataset.key
      if (!key) return

      // pola własne czyścimy jawnie pustą wartością, bo serwer zmienia tylko przekazane klucze;
      // pozostałe formatki nadpisują cały hash fields, więc tam brak klucza dalej czyści
      const clearEmpty = input.dataset.clearEmpty === "true"

      if (input.dataset.multiple === "true") {
        // multi-select (grupa checkboxów pod jednym kluczem): zbieramy zaznaczone wartości do tablicy.
        // przetwarzamy każdy klucz raz - iterujemy po wszystkich checkboxach tej samej grupy
        if (multipleDone.has(key)) return
        multipleDone.add(key)
        const values = this.fieldTargets
          .filter((el) => el.dataset.key === key && el.checked)
          .map((el) => el.value)
        if (values.length === 0 && !clearEmpty) {
          deleteKey(data, key)
        } else {
          writeKey(data, key, values)
        }
      } else if (input.type === "checkbox") {
        writeKey(data, key, input.checked)
      } else {
        const value = input.value.trim()
        if (value === "" && !clearEmpty) {
          deleteKey(data, key)
        } else {
          writeKey(data, key, value)
        }
      }
    })

    const json = JSON.stringify(data, null, 2)
    this.jsonTarget.value = json

    const cmWrapper = this.jsonTarget.closest("[data-controller='codemirror']")
    if (cmWrapper && cmWrapper.cmView) {
      const view = cmWrapper.cmView
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: json }
      })
    }
  }

  parseJson() {
    try {
      return JSON.parse(readRaw(this.jsonTarget) || "{}")
    } catch {
      return null
    }
  }

  // --- Tablica (domains itp.) ---

  // wczytaj pierwszy element tablicy do inputa
  syncArrayFields() {
    this.arrayFieldTargets.forEach((input) => {
      const textarea = this.findArrayTextarea(input)
      if (!textarea) return

      const arr = this.parseArrayJson(textarea)
      if (arr && arr.length > 0) {
        input.value = arr[0]
      }
    })
  }

  // po zmianie inputa — ustaw [value] jako pierwszy element tablicy
  arrayFieldChanged(event) {
    if (this.updating) return
    this.updating = true

    const input = event.target
    const textarea = this.findArrayTextarea(input)
    if (!textarea) { this.updating = false; return }

    const arr = this.parseArrayJson(textarea) || []
    const value = input.value.trim()

    if (value === "") {
      arr.shift()
    } else if (arr.length === 0) {
      arr.push(value)
    } else {
      arr[0] = value
    }

    const json = JSON.stringify(arr, null, 2)
    textarea.value = json

    const cmWrapper = textarea.closest("[data-controller='codemirror']")
    if (cmWrapper && cmWrapper.cmView) {
      const view = cmWrapper.cmView
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: json }
      })
    }

    this.updating = false
  }

  // po zmianie textarea tablicy (np. z CodeMirror) — zaktualizuj input
  arrayJsonChanged(event) {
    if (this.updating) return
    this.updating = true

    const textarea = event.target
    const id = textarea.id
    const input = this.arrayFieldTargets.find((f) => f.dataset.arrayFor === id)
    if (input) {
      const arr = this.parseArrayJson(textarea)
      input.value = (arr && arr.length > 0) ? arr[0] : ""
    }

    this.updating = false
  }

  mergeArrayFieldsToJson() {
    this.arrayFieldTargets.forEach((input) => {
      const textarea = this.findArrayTextarea(input)
      if (!textarea) return

      const arr = this.parseArrayJson(textarea) || []
      const value = input.value.trim()

      if (value === "" && arr.length > 0) {
        arr.shift()
      } else if (value !== "" && arr.length === 0) {
        arr.push(value)
      } else if (value !== "") {
        arr[0] = value
      }

      textarea.value = JSON.stringify(arr, null, 2)
    })
  }

  findArrayTextarea(input) {
    const targetId = input.dataset.arrayFor
    return targetId ? document.getElementById(targetId) : null
  }

  parseArrayJson(textarea) {
    try {
      const parsed = JSON.parse(readRaw(textarea) || "[]")
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
}

// Tekst JSON-a z textarea albo z CodeMirror, gdy edytor jest podpięty
function readRaw(textarea) {
  const cmWrapper = textarea.closest("[data-controller='codemirror']")
  return (cmWrapper && cmWrapper.cmView) ? cmWrapper.cmView.state.doc.toString() : textarea.value
}

// Czy tekst to poprawny JSON danego rodzaju ("object" / "array"); pusty = poprawny (pusty obiekt/tablica)
export function isValidJson(raw, kind) {
  if (!raw || raw.trim() === "") return true
  try {
    const parsed = JSON.parse(raw)
    if (kind === "array") return Array.isArray(parsed)
    return parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)
  } catch {
    return false
  }
}

// Odczyt/zapis klucza fields; "a.b.c" to ścieżka w zagnieżdżonych obiektach
export function readKey(data, key) {
  return key.split(".").reduce((obj, part) => (obj && typeof obj === "object" ? obj[part] : undefined), data)
}

export function writeKey(data, key, value) {
  const parts = key.split(".")
  const last = parts.pop()
  let obj = data
  parts.forEach((part) => {
    if (!obj[part] || typeof obj[part] !== "object" || Array.isArray(obj[part])) obj[part] = {}
    obj = obj[part]
  })
  obj[last] = value
}

// Usuwa tylko sam klucz - puste obiekty nadrzędne zostają, bo serwer scalający fields
// (pola własne projektu) po braku klucza najwyższego poziomu zostawiłby starą wartość
export function deleteKey(data, key) {
  const parts = key.split(".")
  const last = parts.pop()
  const parent = parts.length ? readKey(data, parts.join(".")) : data
  if (parent && typeof parent === "object") delete parent[last]
}
